import type { AnalyticsBrowser } from "@segment/analytics-next"
import { default as Cookies } from "js-cookie"

const shareIdKey = "shareId"
const sharedBuyerGuideKey = "sharedBuyerGuide"
const isMarketplaceUserKey = "isMarketplaceUser"
const optedInViaKey = "optedInVia"

const cacheValuesToSessionStorage = () => {
  const location = window.location

  const searchParams = new URLSearchParams(location.search)
  const shareId = searchParams.get("shareId")

  if (shareId) {
    sessionStorage.setItem(shareIdKey, shareId)
  }

  if (location.pathname.startsWith("/buyer-guides/") && !location.pathname.startsWith("/buyer-guides/product/")) {
    const supplierSlug = location.pathname.replace(/^\/buyer-guides\//, "")?.trim()

    if (supplierSlug) {
      sessionStorage.setItem(sharedBuyerGuideKey, supplierSlug)
    }
  }

  const isMarketplaceUser = searchParams.get("marketplaceLogin")
  if (isMarketplaceUser) {
    sessionStorage.setItem(isMarketplaceUserKey, isMarketplaceUser)
  }

  const optedInVia = searchParams.get("optedInVia")
  if (optedInVia) {
    sessionStorage.setItem(optedInViaKey, optedInVia)
  }
}

export const prepareMarketingProperties = () => {
  cacheValuesToSessionStorage()

  // Set up a broad bucket of name/value pairs
  // The app can be made aware of.
  const properties = []

  // In order to thread anonymous user data into our account, grab a persistent
  // user ID from Segment. We'll use this after analytics load to associate the
  // Mixpanel anonymous user ID with this value.
  const anonymousId = getSegmentAnonymousUserId()

  if (anonymousId) {
    properties.push({
      name: "anonymousId",
      value: anonymousId,
    })
  }

  const ft_utm_source = Cookies.get("ft_utm_source")
  const ft_utm_medium = Cookies.get("ft_utm_medium")
  const ft_utm_campaign = Cookies.get("ft_utm_campaign")
  const ft_utm_content = Cookies.get("ft_utm_content")
  const ft_utm_term = Cookies.get("ft_utm_term")
  const ft_referer = Cookies.get("ft_referer")

  if (ft_utm_source) {
    properties.push({
      name: "ft_utm_source",
      value: ft_utm_source,
    })
  }

  if (ft_utm_medium) {
    properties.push({
      name: "ft_utm_medium",
      value: ft_utm_medium,
    })
  }

  if (ft_utm_campaign) {
    properties.push({
      name: "ft_utm_campaign",
      value: ft_utm_campaign,
    })
  }

  if (ft_utm_content) {
    properties.push({
      name: "ft_utm_content",
      value: ft_utm_content,
    })
  }

  if (ft_utm_term) {
    properties.push({
      name: "ft_utm_term",
      value: ft_utm_term,
    })
  }

  if (ft_referer) {
    properties.push({
      name: "ft_referer",
      value: ft_referer,
    })
  }

  const shareId = sessionStorage.getItem(shareIdKey)

  if (shareId) {
    properties.push({
      name: shareIdKey,
      value: shareId,
    })
  }

  const sharedBuyerGuide = sessionStorage.getItem(sharedBuyerGuideKey)

  if (sharedBuyerGuide) {
    properties.push({
      name: sharedBuyerGuideKey,
      value: sharedBuyerGuide,
    })
  }

  const isMarketplaceUser = sessionStorage.getItem(isMarketplaceUserKey)

  if (isMarketplaceUser) {
    properties.push({
      name: isMarketplaceUserKey,
      value: isMarketplaceUser,
    })
  }

  const optedInVia = sessionStorage.getItem(optedInViaKey)

  if (optedInVia) {
    properties.push({
      name: optedInViaKey,
      value: optedInVia,
    })
  }

  return properties
}

function getSegmentAnonymousUserId(): string | null {
  // Look for the AJS anonymous ID. If we have it, we can avoid creating more
  // IDs (and getting charged for more monthly users in Mutiny.)
  let id = window.localStorage.getItem("ajs_anonymous_id")

  if (id) {
    id = JSON.parse(id)

    if (IS_DEVELOPMENT) {
      console.log(`got ID from analytics.js: ${id}`)
    }

    // Segment stores the anonymous user ID as a JSON string in localStorage
    return id
  }

  // If we don't have the anonymous ID in localStorage, we may have it in a
  // cookie (scoped to vendr.com.) This is likely to happen if someone clicked
  // "login" on the marketing site.
  id = Cookies.get("ajs_anonymous_id") || null

  if (id) {
    if (IS_DEVELOPMENT) {
      console.log(`got ID from analytics cookie: ${id}`)
    }

    return id
  }

  return null
}

export async function unifyAnonymousIds(analytics: AnalyticsBrowser): Promise<void> {
  const anonymousId = getSegmentAnonymousUserId()

  if (!anonymousId) {
    // No use trying to associate Mixpanel/Segment IDs with `null`! This should
    // basically never happen, but it *can*, however slim the possibility.
    return
  }

  try {
    if (window.mixpanel) {
      const distinctMixpanelId = window.mixpanel.get_distinct_id()

      await analytics.alias(distinctMixpanelId, anonymousId)
      console.debug(`associated distinct Mixpanel ID ${distinctMixpanelId} with first-party ID ${anonymousId}`)
    }
  } catch (e) {
    console.log("Could not alias Mixpanel ID", e)
  }
}
